import React, { useMemo, useState } from 'react';
import { message, Space, Typography } from 'antd';
import { aiConfigApi } from '../../../../../lib/aiConfigApi';
import ImportConversationUploadCard from './ImportConversationUploadCard';
import ParsedQaEditor from './ParsedQaEditor';
import FaqEnrichmentReviewModal from './FaqEnrichmentReviewModal';
import AgentConfigReviewModal from './AgentConfigReviewModal';
import {
  buildApplyPayload,
  normalizeAnalyzeResponse,
  normalizeFaqDraft,
  normalizeParsedPair,
  normalizeStyleCandidate,
} from './sessionMappers';

const { Text } = Typography;

export default function ConversationAgentBuilderTab({ companyId, userName, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [faqModalVisible, setFaqModalVisible] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [uploadState, setUploadState] = useState({
    fileName: '',
    fileSize: 0,
    uploadedAt: '',
    rawText: '',
    sessionId: null,
  });
  const [parsedPairs, setParsedPairs] = useState([]);
  const [faqDrafts, setFaqDrafts] = useState([]);
  const [styleCandidates, setStyleCandidates] = useState([]);
  const [selectedStyleCandidateId, setSelectedStyleCandidateId] = useState(null);
  const [configDraft, setConfigDraft] = useState({});
  const [fieldConfidenceMap, setFieldConfidenceMap] = useState({});

  const analyzeDisabled = useMemo(() => {
    return (
      parsedPairs.filter((pair) => !pair.isRemoved && pair.questionRaw && pair.answerRaw)
        .length === 0 || !uploadState.sessionId
    );
  }, [parsedPairs, uploadState.sessionId]);

  const handleUpload = async (file) => {
    const rawText = await file.text();
    setLoading(true);
    try {
      const sessionRes = await aiConfigApi.createFaqImportSession({
        companyId,
        fileName: file.name,
        rawText,
        createdBy: userName,
      });
      const parseRes = await aiConfigApi.parseFaqImport(sessionRes.data.id, rawText);

      setUploadState({
        fileName: file.name,
        fileSize: file.size,
        uploadedAt: new Date().toLocaleString('vi-VN'),
        rawText,
        sessionId: sessionRes.data.id,
      });
      setParsedPairs((parseRes.data || []).map(normalizeParsedPair));
      setFaqDrafts([]);
      setStyleCandidates([]);
      setSelectedStyleCandidateId(null);
      setConfigDraft({});
      setFieldConfidenceMap({});
      message.success('Đã parse file thành công');
    } catch (error) {
      console.error(error);
      message.error('Không thể import file hội thoại');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setUploadState({
      fileName: '',
      fileSize: 0,
      uploadedAt: '',
      rawText: '',
      sessionId: null,
    });
    setParsedPairs([]);
    setFaqDrafts([]);
    setStyleCandidates([]);
    setSelectedStyleCandidateId(null);
    setConfigDraft({});
    setFieldConfidenceMap({});
    setFaqModalVisible(false);
    setConfigModalVisible(false);
  };

  const handleAnalyze = async () => {
    if (!uploadState.sessionId) return;
    setAnalyzing(true);
    try {
      await aiConfigApi.updateParsedPairs(uploadState.sessionId, parsedPairs);
      const analysisRes = await aiConfigApi.analyzeFaqImport(uploadState.sessionId);
      const normalized = normalizeAnalyzeResponse(analysisRes.data);
      const nextFaqDrafts = normalized.faqDrafts.map(normalizeFaqDraft);
      const nextStyleCandidates = normalized.styleCandidates.map(
        normalizeStyleCandidate,
      );

      setFaqDrafts(nextFaqDrafts);
      setStyleCandidates(nextStyleCandidates);
      setSelectedStyleCandidateId(nextStyleCandidates[0]?.id || null);
      setConfigDraft(normalized.configDraft);
      setFieldConfidenceMap(normalized.fieldConfidenceMap);
      setFaqModalVisible(true);
      message.success('Đã phân tích bằng AI');
    } catch (error) {
      console.error(error);
      message.error('Không thể phân tích file bằng AI');
    } finally {
      setAnalyzing(false);
    }
  };

  const saveFaqDrafts = async () => {
    if (!uploadState.sessionId) return;
    await aiConfigApi.updateFaqDrafts(uploadState.sessionId, faqDrafts);
    message.success('Đã lưu nháp FAQ');
  };

  const saveConfigDraft = async () => {
    if (!uploadState.sessionId) return;
    await aiConfigApi.updateConfigDraft(uploadState.sessionId, {
      selectedStyleCandidateId,
      configDraft,
    });
    message.success('Đã lưu nháp cấu hình Agent');
  };

  const applyDraft = async () => {
    if (!uploadState.sessionId) return;
    const payload = buildApplyPayload({
      companyId,
      faqDrafts,
      configDraft,
      selectedStyleCandidateId,
      appliedBy: userName,
    });
    await aiConfigApi.applyFaqImport(uploadState.sessionId, payload);
    message.success('Đã lưu và áp dụng cấu hình mới');
    setConfigModalVisible(false);
    setFaqModalVisible(false);
    onApplied?.();
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1a1a1a', marginBottom: 8 }}>
          Tạo Agent từ Hội thoại
        </div>
        <Text type="secondary">
          Upload file .txt, kiểm tra parse thô rồi chạy AI để dựng FAQ và cấu hình Agent nháp.
        </Text>
      </div>

      <ImportConversationUploadCard
        loading={loading}
        fileName={uploadState.fileName}
        fileSize={uploadState.fileSize}
        uploadedAt={uploadState.uploadedAt}
        onUpload={handleUpload}
        onReset={handleReset}
      />

      <ParsedQaEditor
        parsedPairs={parsedPairs}
        onChange={setParsedPairs}
        analyzeDisabled={analyzeDisabled}
        analyzing={analyzing}
        onAnalyze={handleAnalyze}
      />

      <FaqEnrichmentReviewModal
        visible={faqModalVisible}
        faqDrafts={faqDrafts}
        onChange={setFaqDrafts}
        onBack={() => setFaqModalVisible(false)}
        onNext={() => {
          setFaqModalVisible(false);
          setConfigModalVisible(true);
        }}
        onSaveDraft={saveFaqDrafts}
      />

      <AgentConfigReviewModal
        visible={configModalVisible}
        configDraft={configDraft}
        styleCandidates={styleCandidates}
        selectedStyleCandidateId={selectedStyleCandidateId}
        fieldConfidenceMap={fieldConfidenceMap}
        onChange={({ configDraft: nextConfigDraft, selectedStyleCandidateId: nextSelectedStyleCandidateId }) => {
          if (nextConfigDraft) {
            setConfigDraft(nextConfigDraft);
          }
          if (nextSelectedStyleCandidateId !== undefined) {
            setSelectedStyleCandidateId(nextSelectedStyleCandidateId);
          }
        }}
        onApply={applyDraft}
        onBack={() => {
          setConfigModalVisible(false);
          setFaqModalVisible(true);
        }}
        onSaveDraft={saveConfigDraft}
      />
    </Space>
  );
}
